import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown, Scissors } from "lucide-react";

export default function Auth({ register: isRegister }) {
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(isRegister ? "register" : "login");
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let user;
      if (mode === "register") {
        user = await register(form);
        toast.success("Cadastro realizado! Bem-vindo à Big Black.");
      } else {
        user = await login(form.email, form.password);
        toast.success("Login realizado!");
      }
      nav(user.role === "admin" ? "/admin" : "/agendar");
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Erro ao autenticar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img src="https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 to-black" />
      </div>
      <div className="relative w-full max-w-md bg-zinc-900/90 backdrop-blur border border-zinc-800 rounded-2xl p-8 animate-fade-up">
        <Link to="/" className="flex flex-col items-center mb-6" data-testid="auth-logo">
          <Crown className="w-8 h-8 text-amber-400 mb-2" />
          <span className="font-display text-2xl gold-text font-bold">Barbearia Big Black</span>
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">Desde 2020</span>
        </Link>

        <div className="flex rounded-full bg-zinc-800 p-1 mb-6">
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "login" ? "bg-amber-400 text-black" : "text-zinc-400"}`} onClick={() => setMode("login")} data-testid="auth-tab-login">Entrar</button>
          <button className={`flex-1 py-2 rounded-full text-sm font-semibold transition-colors ${mode === "register" ? "bg-amber-400 text-black" : "text-zinc-400"}`} onClick={() => setMode("register")} data-testid="auth-tab-register">Cadastrar</button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <>
              <div>
                <Label className="text-zinc-300">Nome</Label>
                <Input value={form.name} onChange={set("name")} required placeholder="Seu nome" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="auth-name" />
              </div>
              <div>
                <Label className="text-zinc-300">WhatsApp / Telefone</Label>
                <Input value={form.phone} onChange={set("phone")} required placeholder="(11) 99999-9999" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="auth-phone" />
              </div>
            </>
          )}
          <div>
            <Label className="text-zinc-300">E-mail</Label>
            <Input type="email" value={form.email} onChange={set("email")} required placeholder="voce@email.com" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="auth-email" />
          </div>
          <div>
            <Label className="text-zinc-300">Senha</Label>
            <Input type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" className="mt-1 bg-zinc-800 border-zinc-700" data-testid="auth-password" />
          </div>
          {mode === "login" && (
            <div className="text-right -mt-1">
              <Link to="/esqueci-senha" className="text-xs text-amber-400 hover:underline" data-testid="forgot-password-link">Esqueci minha senha</Link>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full h-12 hover:brightness-110" data-testid="auth-submit">
            <Scissors className="w-4 h-4 mr-2" /> {loading ? "Aguarde..." : mode === "register" ? "Criar Conta" : "Entrar"}
          </Button>
        </form>
        <Link to="/" className="block text-center text-zinc-500 text-sm mt-6 hover:text-amber-400">← Voltar ao início</Link>
      </div>
    </div>
  );
}
